import React from "react";
import ChatSkeleton from "../../Skeletons/ChatSkeleton";
import chatStyle from "../../styles/ChatScreen.module.css";

interface IChatMessage {
  _id: string;
  clinicId: string;
  patientId: string;
  from: string;
  to: string;
  message: string;
  createdTimestamp: number;
}

interface IChatMessagesProps {
  chatData: IChatMessage[];
  loading: boolean;
  chatScreenRef: React.RefObject<HTMLDivElement>;
  patientId?: string;
}

const ChatMessages: React.FC<IChatMessagesProps> = ({
  chatData,
  loading,
  chatScreenRef,
  patientId,
}) => {
  // Format time: e.g. 02:35 PM
  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format date header: e.g. Wednesday, May 21, 2025
  const formatDateHeader = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Group messages by date
  const groupMessagesByDate = (messages: IChatMessage[]) => {
    const groups: Record<string, IChatMessage[]> = {};

    messages.forEach((message) => {
      const date = new Date(message.createdTimestamp);
      const dateKey = date.toISOString().split("T")[0]; // YYYY-MM-DD format

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }

      groups[dateKey].push(message);
    });

    return groups;
  };

  if (loading) {
    return <ChatSkeleton count={5} />;
  }

  if (!chatData || chatData.length === 0) {
    return <div className={chatStyle.emptyChat}>No messages yet</div>;
  }

  const groupedMessages = groupMessagesByDate(chatData);
  const sortedDates = Object.keys(groupedMessages).sort();

  return (
    <div className={chatStyle.chatScreenContainer} ref={chatScreenRef}>
      {sortedDates.map((dateKey) => (
        <div key={dateKey} className={chatStyle.dateGroup}>
          <div className={chatStyle.dateHeader}>
            {formatDateHeader(groupedMessages[dateKey][0].createdTimestamp)}
          </div>

          {groupedMessages[dateKey].map((msg) => {
            const isFromClinic =
              msg.from === msg.clinicId && msg.to === msg.patientId;

            return (
              <div
                key={msg._id}
                className={
                  isFromClinic
                    ? chatStyle.receivedMessage // Clinic messages on left
                    : chatStyle.sentMessage // Patient messages on right
                }
              >
                <p className={chatStyle.messageText}>{msg.message}</p>
                <span
                  className={chatStyle.timestamp}
                  style={{
                    fontSize: "0.75rem",
                    color: "#666",
                    marginTop: "4px",
                    textAlign: isFromClinic ? "left" : "right", // Clinic left, Patient right
                    display: "block",
                  }}
                >
                  {formatTimestamp(msg.createdTimestamp)}
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default ChatMessages;
